import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from './http.service';
import {
  Group,
  GroupRole,
  GroupUser,
  GroupUserPicklistOption,
  GroupsListResponse,
  GroupSubmitPayload,
  DEFAULT_GROUPS_CURRENT_PAGE,
  DEFAULT_GROUPS_PER_PAGE,
} from '../../store/entities/models/group.model';
import { transformSnakeToCamel, transformCamelToSnake } from '../../store/utils/transformers';
import { ApiTableFilter } from '../../shared/models/table-filter.model';

/**
 * The domain-users picklist body shape is not consistent across environments,
 * so unwrap defensively. The rows (each `{ value, label }`) may live at the top
 * level as an array, or nested one or two levels deep under `data`/`list`:
 *   [ ... ]
 *   { data: [ ... ] }
 *   { data: { data: [ ... ] } }
 *   { data: { list: [ ... ] } }
 *   { list: [ ... ] }
 * Returns the first array found, or [] when none matches.
 */
interface PicklistEnvelope {
  data?: unknown;
  list?: unknown;
}

interface PicklistRow {
  value?: string;
  label?: string;
  uuid?: string;
  id?: string;
  name?: string;
  fullName?: string;
}

function extractPicklistRows(response: unknown): Record<string, unknown>[] {
  const asArray = (value: unknown): Record<string, unknown>[] | null =>
    Array.isArray(value) ? (value as Record<string, unknown>[]) : null;

  const topLevel = asArray(response);

  if (topLevel) {
    return topLevel;
  }

  const root = (response ?? {}) as PicklistEnvelope;
  const nested = (root.data ?? {}) as PicklistEnvelope;

  const candidates: unknown[] = [root.data, root.list, nested.data, nested.list];

  for (const candidate of candidates) {
    const rows = asArray(candidate);

    if (rows) {
      return rows;
    }
  }

  return [];
}

export interface GroupsListParams {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
  sort_direction?: string;
  filters?: ApiTableFilter[];
  filter_link?: string;
}

/**
 * Groups API. Mirrors the React `services/Groups/Groups.js` contract: endpoints
 * live under `/groups` (and `/group_roles`, `/bulk-action/groups/delete`) and are
 * NOT scoped to the active domain by a `/{domainId}` prefix.
 *
 * The wire format is snake_case; this service converts to/from camelCase at the
 * boundary via the shared transformers.
 */
@Injectable({
  providedIn: 'root',
})
export class GroupsService {
  private http = inject(HttpService);

  getGroupsList(params: GroupsListParams = {}): Observable<GroupsListResponse> {
    const queryParams: Record<string, unknown> = {
      page: params.page ?? DEFAULT_GROUPS_CURRENT_PAGE,
      per_page: params.per_page ?? DEFAULT_GROUPS_PER_PAGE,
      sort: params.sort ?? 'created_at',
      sort_direction: params.sort_direction ?? 'desc',
      search: params.search ?? '',
    };

    if (params.filters?.length) {
      queryParams.filters = params.filters;
      queryParams.filter_link = params.filter_link ?? 'and';
    }

    return this.http
      .get<Record<string, unknown>>('/groups', queryParams)
      .pipe(map((response) => transformSnakeToCamel(response) as GroupsListResponse));
  }

  getGroup(uuid: string): Observable<Group> {
    return this.http
      .get<{ data: Record<string, unknown> }>(`/groups/${uuid}`)
      .pipe(map((response) => transformSnakeToCamel(response.data ?? {}) as Group));
  }

  getGroupRoles(): Observable<GroupRole[]> {
    return this.http
      .get<{ data: Record<string, unknown>[] }>('/group_roles')
      .pipe(
        map((response) =>
          (response.data ?? []).map((role) => transformSnakeToCamel(role) as GroupRole)
        )
      );
  }

  getGroupUsers(uuid: string): Observable<GroupUser[]> {
    return this.http
      .get<{ data: Record<string, unknown>[] }>(`/groups/${uuid}/users`)
      .pipe(
        map((response) =>
          (response.data ?? []).map((user) => transformSnakeToCamel(user) as GroupUser)
        )
      );
  }

  getDomainUsersPicklist(domainUuid: string): Observable<GroupUserPicklistOption[]> {
    return this.http
      .get<unknown>(`/picklist/domains/${domainUuid}/users`)
      .pipe(
        map((response) =>
          extractPicklistRows(response).map((option) => {
            const row = transformSnakeToCamel(option) as PicklistRow;
            // Rows normally come as { value, label }; tolerate common aliases
            // (uuid/id for value, name/fullName for label) so the multiselect
            // (textField="label", valueField="value") always has both keys.
            const value = row.value ?? row.uuid ?? row.id ?? '';
            const label = row.label ?? row.name ?? row.fullName ?? value;

            return { value, label };
          })
        )
      );
  }

  createGroup(payload: GroupSubmitPayload): Observable<{ data: Group }> {
    return this.http.post<{ data: Group }>(
      '/groups',
      transformCamelToSnake(payload) as Record<string, unknown>
    );
  }

  updateGroup(uuid: string, payload: GroupSubmitPayload): Observable<{ data: Group }> {
    return this.http.patch<{ data: Group }>(
      `/groups/${uuid}`,
      transformCamelToSnake(payload) as Record<string, unknown>
    );
  }

  deleteGroup(uuid: string): Observable<void> {
    return this.http.delete<void>(`/groups/${uuid}`);
  }

  bulkDeleteGroups({ uuids, allSelected }: { uuids: string[]; allSelected: boolean }): Observable<void> {
    return this.http.post<void>('/bulk-action/groups/delete', {
      uuids,
      all_selected: allSelected,
    });
  }
}
